import {
  DATE_BUCKET_OPTIONS,
  DATE_TRUNC_FUNCTION_NAME,
  DateBucketValueType,
  DateTruncExpression,
  EXPLODE_FUNCTION_NAME,
  ExpressionTypes,
  FormatterType,
} from '@fabric-ui/components';
import { ExplodeExpression } from '../types';

const COLLECT_DISTINCT = 'COLLECT_DISTINCT';
const COLLECT_DISTINCT_FLAT = 'COLLECT_DISTINCT_FLAT';

export function isDateTruncExpression(name: string): boolean;
export function isDateTruncExpression(expression: ExpressionTypes): expression is DateTruncExpression;
export function isDateTruncExpression(arg: string | ExpressionTypes): boolean {
  if (typeof arg === 'string') {
    return arg === DATE_TRUNC_FUNCTION_NAME;
  }
  if (typeof arg === 'object' && arg !== null && 'function' in arg && arg.function) {
    return arg.function.name === DATE_TRUNC_FUNCTION_NAME;
  }
  return false;
}
export function isExplodeExpression(name: string): boolean;
export function isExplodeExpression(expression: ExpressionTypes): expression is ExplodeExpression;
export function isExplodeExpression(arg: string | ExpressionTypes): boolean {
  if (typeof arg === 'string') {
    return arg === EXPLODE_FUNCTION_NAME;
  }
  if (typeof arg === 'object' && arg !== null && 'function' in arg && arg.function) {
    return arg.function.name === EXPLODE_FUNCTION_NAME;
  }
  return false;
}

export const getDateTruncExpression = (fieldName: string, bucket: DateBucketValueType): DateTruncExpression => ({
  function: {
    name: DATE_TRUNC_FUNCTION_NAME,
    args: [
      {
        symbol: {
          ref: fieldName,
        },
      },
      {
        stringLiteral: {
          value: bucket,
        },
      },
    ],
  },
});

export const getDateBucketFromExpression = (expression: DateTruncExpression) => expression.function.args[1].stringLiteral.value;

export const getSimplifiedDateTruncExpression = (fieldName: string, bucket: string) => {
  const alias = `${fieldName}_${bucket}`;

  return {
    [fieldName]: {
      name: fieldName,
      function: DATE_TRUNC_FUNCTION_NAME,
      bucket,
      alias,
    },
  };
};

export const getSimplifiedExplodeExpression = (dimName: string) => ({
  [dimName]: {
    name: dimName,
    function: EXPLODE_FUNCTION_NAME,
    alias: `${dimName}_${EXPLODE_FUNCTION_NAME}`,
  },
});

export const getSimplifiedExpression = (dimName: string, expression: ExpressionTypes) => {
  if (isDateTruncExpression(expression)) {
    return getSimplifiedDateTruncExpression(dimName, getDateBucketFromExpression(expression));
  }

  if (isExplodeExpression(expression)) {
    return getSimplifiedExplodeExpression(dimName);
  }

  return undefined;
};

export const getFieldTypeByExpression = (expression): FormatterType | undefined => {
  if (isDateTruncExpression(expression.function)) {
    return FormatterType.historicDate;
  }

  if (isExplodeExpression(expression.function)) {
    return FormatterType.string;
  }

  return undefined;
};

export const checkIsNoneDateBucket = (
  bucket:
    | typeof DATE_BUCKET_OPTIONS.day.value
    | typeof DATE_BUCKET_OPTIONS.week.value
    | typeof DATE_BUCKET_OPTIONS.month.value
    | typeof DATE_BUCKET_OPTIONS.year.value
    | typeof DATE_BUCKET_OPTIONS.none.value
) => bucket === DATE_BUCKET_OPTIONS.none.value;

export const getDimWithExpressionTitle = (dimTitle, bucket: keyof typeof DATE_BUCKET_OPTIONS) =>
  checkIsNoneDateBucket(bucket) ? dimTitle : `${dimTitle} (${DATE_BUCKET_OPTIONS[bucket]?.title})`;

export const getExpressionFunctionName = (expression: ExpressionTypes): string => expression.function.name;

export const getFieldTitleByExpression = ({
  expression,
  displayName,
}: {
  expression: ExpressionTypes;
  displayName: string | number | null | undefined;
}): string => {
  if (isDateTruncExpression(expression)) {
    return getDimWithExpressionTitle(displayName, getDateBucketFromExpression(expression));
  }

  if (isExplodeExpression(expression)) {
    return `${displayName} (Expended)`;
  }

  return 'no title';
};

export const getExpressionArgs = (expression: ExpressionTypes): unknown[] | undefined => expression.function.args;
export const getCollectExpression = (dimName: string, isRepeated: boolean) => ({
  function: {
    name: 'ARRAY_SLICE',
    args: [
      {
        function: {
          name: isRepeated ? COLLECT_DISTINCT_FLAT : COLLECT_DISTINCT,
          args: [
            {
              symbol: {
                ref: dimName,
              },
            },
          ],
        },
      },
      {
        number: {
          value: 0,
        },
      },
      {
        number: {
          value: 25,
        },
      },
    ],
  },
});

export const getExplodeExpression = (dimName: string): ExplodeExpression => ({
  function: {
    name: EXPLODE_FUNCTION_NAME,
    args: [
      {
        symbol: {
          ref: dimName,
        },
      },
    ],
  },
});

export const getExpression = (fieldName: string, functionName: string, bucket: DateBucketValueType) => {
  if (isDateTruncExpression(functionName)) {
    return getDateTruncExpression(fieldName, bucket);
  }

  if (isExplodeExpression(functionName)) {
    return getExplodeExpression(fieldName);
  }

  return undefined;
};
